// CRUD admin des packs de crédits IA. La table en base est la source de
// vérité du catalogue : le checkout Stripe construit son price_data depuis
// ces valeurs, une modification ici est effective immédiatement (aucun
// Product/Price Stripe à synchroniser). Pattern : aiConfigService.
import { toast } from "sonner";

const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/admin`;

export interface AdminAiCreditPack {
  id: string;
  name: string;
  description: string | null;
  priceEur: number;
  priceCents: number;
  creditsUsd: number;
  creditsMicroCents: number;
  isActive: boolean;
  displayOrder: number;
  updatedAt: string;
}

export interface AiCreditPackPayload {
  name?: string;
  description?: string | null;
  priceEur?: number;
  creditsUsd?: number;
  isActive?: boolean;
  displayOrder?: number;
}

async function adminFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: { "Content-Type": "application/json", ...options.headers },
  });
  if (response.status === 401) {
    toast.error("Session administrateur expirée. Redirection en cours...");
    setTimeout(() => {
      window.location.href = "/admin";
    }, 1000);
    throw new Error("Session expirée");
  }
  return response;
}

async function parseError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await res.json()) as { message?: string };
    if (body.message) message = body.message;
  } catch {
    // garder le fallback
  }
  throw new Error(message);
}

export const aiCreditPacksAdminService = {
  list: async (): Promise<AdminAiCreditPack[]> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-credit-packs`, {
      method: "GET",
    });
    if (!res.ok) await parseError(res, "Échec du chargement des packs");
    const body = (await res.json()) as { data: AdminAiCreditPack[] };
    return body.data;
  },

  create: async (payload: AiCreditPackPayload): Promise<AdminAiCreditPack> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-credit-packs`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!res.ok) await parseError(res, "Échec de la création du pack");
    const body = (await res.json()) as { data: AdminAiCreditPack };
    return body.data;
  },

  update: async (
    id: string,
    payload: AiCreditPackPayload,
  ): Promise<AdminAiCreditPack> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-credit-packs/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    if (!res.ok) await parseError(res, "Échec de la mise à jour du pack");
    const body = (await res.json()) as { data: AdminAiCreditPack };
    return body.data;
  },

  remove: async (id: string): Promise<void> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-credit-packs/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) await parseError(res, "Échec de la suppression du pack");
  },
};
